// Filmul împachetării: un cadru pe pas, scris de solverul pe benzi în
// `diagnostics.depanare.film` și desenat de ecranul «Încercări parcare»
// (`FilmLayer`). Aici stau doar formele lui și citirea din diagnostice.

/** O alee într-un cadru de film. `noua`: n-avea corespondent pe aceeași axă în
 *  cadrul dinainte. `sursa`: linia de schelet din care vine (numai aleile care
 *  stau pe o axă desenată). */
export type AleeFilm = { x1: number; y1: number; x2: number; y2: number;
                         noua: boolean; sursa?: string; w?: number };
export type Segment = { x1: number; y1: number; x2: number; y2: number };
/** Un cadru al filmului de împachetare (`diagnostics.depanare.film`): rețeaua
 *  ÎNTREAGĂ după pasul `pas`, plus ce s-a schimbat față de cadrul dinainte.
 *  `spatii` e forma compactă a locurilor: `[cx, cy, deg, forma]`. */
export type CadruFilm = {
  pas: string; alei: AleeFilm[]; scoase: Segment[]; adaugate: number;
  locuri?: number; spatii?: [number, number, number, string][];
  nota?: string; subsol?: string;
  /** Cât a durat pasul, în secunde (02.10.2026). La pașii căutării e timpul de
   *  la nodul dinainte de pe drumul câștigător: cât a stat fereastra întreagă
   *  să înainteze un nivel, nu doar starea asta. */
  durata_s?: number;
  /** Numele paginii HTML de depanare a pasului (artefact `pagina_html`, servit
   *  de `/api/projects/…/solutions/…/pagini/<nume>`): azi căile de evacuare,
   *  un nivel pe pagină (19.10.2026). */
  pagina?: string;
  /** Nivelul cadrului, când nu e al planșei de la −1 (filmul e al ei): pasul
   *  ALA își arată ultimul nivel, unde stau adăposturile. */
  nivel?: number;
  /** Locurile pe care le-a luat pasul — la ALA, cele de sub adăposturi, din
   *  culoare, din puțuri și din fața ușilor SAS —, în forma lui `spatii`. */
  pierdute?: [number, number, number, string][];
  /** Locurile PUSE de pas: cele din `spatii` care nu erau în cadrul dinainte
   *  al aceluiași nivel (02.10.2026, ca numărul din titlu să se poată verifica
   *  pe desen). */
  noi?: [number, number, number, string][];
  /** Contururile camerelor puse de pas (adăposturile ALA și ieșirile lor). */
  camere?: { kind: string; label?: string; poly: number[][] }[];
  /** Cadrul e de la SOL (circulația planului, `engine/circulatie/film.py`):
   *  se desenează pe vederea situației, nu pe planșa subsolului. */
  situatie?: true;
  /** Poligoanele cadrului de la sol: verdele, zonele de acces (±30 m pe latura
   *  terenului în jurul unei intrări), palierele și gurile rampelor, inelul
   *  excavației. */
  poligoane?: { fel: "verde" | "zona" | "palier" | "gura" | "excavatie";
                poly: number[][]; label?: string }[];
};

/** Filmul PARCĂRII (arhitectura), de oriunde a ajuns el: la rădăcina
 *  diagnosticelor (documentul unei încercări) sau în raportul fiecărui subsol
 *  (`diagnostics.structures[i].depanare`, unde o pune `app` — graft-ul pornește
 *  de la documentul etajelor și n-o ia din rezultatul solverului). Cadrele
 *  poartă numele subsolului când sunt mai multe. Cadrele de la SOL (circulația
 *  planului) nu intră aici nici dacă ar ajunge sub aceeași cheie: ele au
 *  cititorul lor, `circulatieDin`. */
export function depanareDin(diag: Record<string, unknown> | null | undefined):
  { film: CadruFilm[] } {
  type Dep = { film?: CadruFilm[] };
  const out: { film: CadruFilm[] } = { film: [] };
  const ia = (d: Dep | null | undefined, subsol?: string) => {
    if (!d) return;
    for (const c of d.film ?? []) if (!c.situatie) out.film.push(subsol ? { ...c, subsol } : c);
  };
  ia(diag?.depanare as Dep | undefined);
  const structuri = (diag?.structures ?? []) as { volumes?: string; depanare?: Dep }[];
  for (const r of structuri) ia(r.depanare, structuri.length > 1 ? r.volumes : undefined);
  return out;
}

/** Filmul CIRCULAȚIEI planului de situație (`diagnostics.depanare.circulatie`,
 *  scris de `positioning_plan/backend/circulatie.grefeaza`): doar cadrele de
 *  la sol, sub cheia lor — separat de filmul parcării, ca ecranele să nu le
 *  poată amesteca (Oana, 28.09.2026). */
export function circulatieDin(diag: Record<string, unknown> | null | undefined): CadruFilm[] {
  const d = diag?.depanare as { circulatie?: CadruFilm[] } | undefined;
  return (d?.circulatie ?? []).filter((c) => c.situatie);
}
